import React, {useMemo} from "react";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import Dependencies from "../../../../../../utilities/Dependencies";
import Environment from "../../../../../../services/interfaces/Environment";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface MythOfTheSixSistersPageProps {
    dependencies: Dependencies,
    env: Environment
}

const MythOfTheSixSistersPage     = (props: MythOfTheSixSistersPageProps) => {

    const page: PageActions = useMemo(() => new PageActions({
        dependencies: props.dependencies,
        env: props.env,
        title: `Myth of the Six Sisters - Chase the Fox`,
        description: "The Myth of the Six Sisters tells the story of how the planet of Casarok was created by a disagreement among six angels!",
        keywords: ["casarok", "myth", "creation myth", "six sisters", "garun'she", "six angels"],
        imgSrc: `chase/world/mythology/star-space.png`,
        url: `world/mythology/myth-of-the-six-sisters`
    }), [props]);

    return page.generateMetaContent(
        <>
            <ContentBox header="Myth of the Six Sisters">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <img className="block width-30-percent"
                         src={page.image().getCdnImageSrc(`chase/world/mythology/star-space.png`)}
                         alt="Star"
                         title="The origin of Garun'she"/>
                    <div className="world-content myth">
                        <p>
                            In the beginning there was nothing. The universe was but an empty void. When the divine
                            beings came to the void, it became like an endless playground for them. There was much chaos
                            as they played as many things came and went through their immense power. To quell the unrest
                            they created, they eventually sought to bring order to this new universe. This task was
                            given to six angelic sisters, deemed worthy by their mastery of the elements. The angels
                            agreed to use their talents to the gods' ends and set forth to fill the great void.
                        </p>
                        <p>
                            The Sister of Fire created massive glowing orbs of flame which came to be the stars in the
                            sky. The Sister of Water fashioned large worlds entirely from water and ice. The Sister of
                            Air fabricated gas giants and streamed bright nebulae like silken fabric in the darkness.
                            The Sister of Earth produced many rocky bodies in the void, especially asteroids and moons.
                            The Sister of Light spread her life-giving power throughout the universe and fabricated
                            various light-producing bodies such as suns, comets, nebulae, and meteors with her sisters.
                            The Sister of Darkness generated black holes in the void to clean up the disorder left by
                            the divine beings and worked with the other sisters as well to glue their works together
                            with gravity so that they might not crash into each other.
                        </p>
                        <p>
                            The process was long and hard, but the sisters worked together tirelessly to fill the great
                            void of the universe with many different and beautiful celestial bodies. The divine beings
                            were pleased with their progress. Eventually, their eyes were caught on one fiery orb in
                            particular which spun and circled about one of the many suns. This ball of flame was so
                            beautiful that they commended the Sister of Fire for her work on it. They dubbed this sphere
                            <strong>Garun'she</strong>, a word in the language of the gods meaning \"beauteous gem\".
                        </p>
                        <p>
                            The other Sisters, however, were jealous of the Sister of Fire. They wanted the glory of
                            this precious gem for themselves. The Sister of Earth struck the first blow and coated
                            Garun'she in thick rock, soil, and rare metals. It glimmered beautifully and was now her
                            work. The Sister of Water was next, covering most of the rock with the freshest of her
                            water. It too sparkled exquisitely in the light of the sun. The Sister of Air then coated
                            the aquatic gem with her finest air, swirling winds, and puffy clouds. It too was very
                            appealing. The Sister of Light gave it magic and put it in the light of her sun. The new
                            presentation was certainly the most praiseworthy of their actions so far. The Sister of
                            Darkness gave their gem a moon to shine upon it at night. However, she did not receive the
                            same praise as her sisters.
                        </p>
                        <p>
                            Rather than coming together and admiring their shared creation, the Sisters instead
                            quarreled over the gem to which they had each tried to lay claim. The fire and earth mixed
                            to become magma. The water swirled among the air to form storms. The moon stole light from
                            the sun to illuminate the dark sky. Each of them threatened to destroy it should another try
                            any tricks. The divine beings were furious when they heard about the squabble and plot to
                            destroy Garun'she. To punish the Sisters for this misdeed of sullying their beloved
                            Garun'she, they were imprisoned together within the largest mountain of the new world they
                            had created.
                        </p>
                        <p>
                            The divine beings then set to work finishing the task that the Sisters had begun. They tried
                            their best to replicate Garun'she in their work, but they never managed to forge a world
                            quite nearly as perfect as their beauteous gem. Instead they all swore to protect the true
                            Garun'she and guard it from harm until the time the universe would become no more. Garun'she
                            came to be known as Casarok in the tongue its inhabitants.
                        </p>
                    </div>
                </div>
            </ContentBox>
        </>
    );
}

export default MythOfTheSixSistersPage;